import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useEsc } from 'hooks/useEsc';
import { pluralise, pluraliseS } from 'lib/pluralise';
import { View } from 'react-native';
import { ButtonToggleGroup } from 'components/ButtonToggleGroup';
import {
  EngageFlatIsHMO,
  EngageFlatIsCommerciallyOwned,
} from 'types/DTOs/BlockManagerDTOs';
import { filterArrayEmpty, rbAddReferencePrefix } from 'lib/riskbase';
import { theme } from 'lib/theme';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  doEngageFlatChangeOccupationStatus,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
} from 'store/actions';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import {
  EngageFlatContact,
  EngageFlatIssues,
  FlatOccupationStatus,
} from 'types/Engage';
import { EngageFlatModalCommunicationsPage } from './EngageFlatModalCommunicationsPage';
import { EngageContactCard } from './Components/EngageContactCard';
import { selectEngageFlat } from 'store/remote/engageSelectors';
import {
  doEngageArchiveContact,
  doEngageFlatArchivedContactsLoad,
  doEngageFlatChangeIsHMO,
  doEngageFlatChangeIsCommerciallyOwned,
  doEngageUnarchiveContact,
} from 'store/remote/engageActions';
type FlatIssueDetailKey = Exclude<
  keyof EngageFlatIssues,
  'hasOwnerIssues' | 'hasResidentIssues'
>;

const FlatIssueDetailContent: Record<
  FlatIssueDetailKey,
  { label: string; message: string }
> = {
  hasNoResidents: {
    label: 'Resident Information Required',
    message: 'Information about the residents living in this flat is required.',
  },
  hasNoOwners: {
    label: 'Owner Information Required',
    message: 'Information about the owners of this flat is required.',
  },
  isMissingResidents: {
    label: 'Missing Resident Contacts',
    message: "There may be other residents but their details aren't recorded.",
  },
  hasExpiredResidentConfirmations: {
    label: 'Resident Information Needs Re-confirming',
    message:
      'All contact information needs to be re-confirmed every 12 months.',
  },
  hasExpiredOwnerConfirmations: {
    label: 'Owner Information Needs Re-confirming',
    message:
      'All contact information needs to be re-confirmed every 12 months.',
  },
  hasEmailIssues: {
    label: 'Email Delivery Issues',
    message:
      'Some contacts have email delivery problems (bounced, rejected or delayed).',
  },
  hasSurveyResponseOverdue: {
    label: 'Contact Survey Response Overdue',
    message: 'A contact survey has been sent but the response is now overdue.',
  },
  requiresContactSurvey: {
    label: 'Contact Survey Required',
    message:
      'A contact survey should be sent to gather current information about the owners and residents of this flat.',
  },
};

const FlatIssueDetailOrder: FlatIssueDetailKey[] = [
  'hasNoResidents',
  'hasNoOwners',
  'isMissingResidents',
  'hasExpiredResidentConfirmations',
  'hasExpiredOwnerConfirmations',
  'hasEmailIssues',
  'hasSurveyResponseOverdue',
  'requiresContactSurvey',
];

export const EngageFlatModal: React.FC<
  ModalScreenProps<ModalScreens.EngageFlat>
> = ({ route }) => {
  const dispatch = useDispatch();

  const { key } = route.params;

  const [activeTab, setActiveTab] = React.useState<'information' | 'messages'>(
    'information',
  );

  const handleClose = React.useCallback(() => {
    dispatch(NAVIGATE_MODAL_BACK());
  }, [dispatch]);

  useEsc(handleClose);

  const {
    property: { id: propertyId },
    flat: { name: flatName, id: flatId },
  } = useSelector((state) => selectEngageFlat(state, key))!;

  const NavigationHeaderMemo = React.useMemo(
    () => (
      <NavigationHeader
        heading={flatName}
        cancelButtonText="Close"
        onPressCancelButton={handleClose}
        menuRight={{
          iconButtonConfig: {
            icon: ICONS.EllipsisLight,
          },
          options: [
            {
              text: 'Send Message',
              onPress: () =>
                dispatch(
                  NAVIGATE_MODAL({
                    screen: ModalScreens.EngageSendCommunication,
                    params: {
                      key,
                      flatIds: [flatId],
                      propertyId,
                      contactId: null,
                      mode: 'singleFlat',
                    },
                  }),
                ),
            },
          ],
        }}
      />
    ),
    [dispatch, flatId, flatName, handleClose, key, propertyId],
  );

  const ButtonToggleGroupMemo = React.useCallback(
    (thisActiveTab: 'information' | 'messages') => (
      <ButtonToggleGroup
        key={thisActiveTab}
        rounded
        buttons={[
          { text: 'Information', key: 'information' },
          { text: 'Messages', key: 'messages' },
        ]}
        active={thisActiveTab}
        onActiveChange={(newActive) => {
          setTimeout(() => setActiveTab(newActive), 1);
          return false;
        }}
        containerStyle={{
          marginBottom: theme.paddingVerticalHalf,
          alignSelf: 'stretch',
          marginHorizontal: theme.paddingHorizontal,
        }}
        size="small"
      />
    ),
    [],
  );

  return (
    <>
      <View
        style={[
          activeTab === 'information' ? { flex: 1 } : { display: 'none' },
        ]}
      >
        <FlatModalInformationTab
          pageKey={key}
          HeaderItems={
            <>
              {NavigationHeaderMemo}
              {ButtonToggleGroupMemo('information')}
            </>
          }
        />
      </View>
      <View
        style={[activeTab === 'messages' ? { flex: 1 } : { display: 'none' }]}
      >
        <EngageFlatModalCommunicationsPage
          pageKey={key}
          contactId={null}
          HeaderItems={
            <>
              {NavigationHeaderMemo}
              {ButtonToggleGroupMemo('messages')}
            </>
          }
        />
      </View>
    </>
  );
};

type FlatIssueRow = { key: string; label: string; message: string };

const FlatModalInformationTab: React.FC<{
  pageKey: string;
  HeaderItems: React.ReactNode;
}> = ({ pageKey: key, HeaderItems }) => {
  const dispatch = useDispatch();
  const { property, flat, contacts, archivedContacts } = useSelector((state) =>
    selectEngageFlat(state, key),
  )!;

  // const hasResident = contacts.some((c) => c.isResident);
  const residentsCount = contacts.filter((c) => c.isResident).length;
  const issueDetailRows: FlatIssueRow[] = FlatIssueDetailOrder.filter(
    (key) => !!flat.issues[key],
  ).map((key) => ({
    key,
    ...FlatIssueDetailContent[key],
  }));

  const handleOpenContact = (contact: EngageFlatContact) => {
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.EngageContact,
        params: { key, contactId: contact.id },
      }),
    );
  };

  const handleOpenCreateContact = () => {
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.EngageContact,
        params: { key, contactId: null },
      }),
    );
  };

  const handleUnarchiveContact = React.useCallback(
    (contactId: string) => {
      dispatch(
        doEngageUnarchiveContact({
          key,
          propertyId: property.id,
          flatId: flat.id,
          contactId,
        }),
      );
    },
    [dispatch, flat.id, key, property.id],
  );

  return (
    <PageScrollView isInModal={true} header={HeaderItems}>
      <Card marginBottomQuarter={!!issueDetailRows.length}>
        <CardRow
          labelAlignTop
          label={flat.name}
          value={[
            property.name,
            property.parentName,
            ...property.addressLines,
            property.postcode,
          ]
            .filter(filterArrayEmpty)
            .join(', ')}
        />
        <ViewWithSeparators horizontal>
          <CardRow
            value={rbAddReferencePrefix(flat.reference)}
            style={{ flex: 1 }}
            icon={ICONS.RBPlectrumSolid}
          />
          <CardRow
            value={flat.customReference}
            style={{ flex: 1 }}
            icon={ICONS.HashtagLight}
          />
        </ViewWithSeparators>
      </Card>

      {!!issueDetailRows.length && (
        <Card>
          {issueDetailRows.map((issue) => (
            <CardRow
              key={issue.key}
              label={issue.label}
              value={issue.message}
              icon={ICONS.TriangleExclamationDuotoneSolid}
              iconColor={theme.colors.red}
              labelAlignTop
            />
          ))}
        </Card>
      )}

      <Card explanation="Notes are for internal use only and are not visible to contacts">
        <ViewWithSeparators horizontal>
          <CardRow
            label="HMO"
            labelAlignTop
            style={{ flex: 1 }}
            onPress={() => dispatch(doEngageFlatChangeIsHMO({ key }))}
            value={
              flat.isHMO === EngageFlatIsHMO.YES
                ? 'Yes'
                : flat.isHMO === EngageFlatIsHMO.NO
                  ? 'No'
                  : 'Not Known'
            }
          />
          <CardRow
            label="Institutional Landlord"
            labelAlignTop
            style={{ flex: 1 }}
            onPress={() =>
              dispatch(doEngageFlatChangeIsCommerciallyOwned({ key }))
            }
            value={
              flat.isCommerciallyOwned === EngageFlatIsCommerciallyOwned.YES
                ? 'Yes'
                : flat.isCommerciallyOwned === EngageFlatIsCommerciallyOwned.NO
                  ? 'No'
                  : 'Not Known'
            }
          />
        </ViewWithSeparators>
        <CardRow label="Internal Notes" placeholder="No Notes" labelAlignTop />
      </Card>

      <Card
        label={pluralise(
          contacts.length,
          '1 Contact',
          '{x} Contacts',
          'Contacts',
        )}
        labelLinkText="Add Contact"
        onLabelLinkPress={handleOpenCreateContact}
        marginBottomQuarter
      >
        <CardRow
          onPress={() => dispatch(doEngageFlatChangeOccupationStatus({ key }))}
          label="Occupation Status"
          value={
            flat.occupationStatus === FlatOccupationStatus.OCCUPIED
              ? residentsCount
                ? `Occupied (${pluraliseS(residentsCount, 'Resident')})`
                : 'Occupied (Residents Not Known)'
              : flat.occupationStatus === FlatOccupationStatus.UNOCCUPIED
                ? 'Unoccupied'
                : 'Not Known'
          }
          valueColor={
            flat.occupationStatus === FlatOccupationStatus.OCCUPIED &&
            !residentsCount
              ? theme.colors.red
              : undefined
          }
        />
      </Card>

      <Card marginBottomQuarter={archivedContacts === null}>
        {!contacts.length && <CardRow value="No Contacts" />}
        {contacts.map((contact) => (
          <EngageContactCard
            key={contact.id}
            contact={contact}
            flatListContact={false}
            showAddedInfo
            showArchivedInfo
            onPress={handleOpenContact}
            menuOptions={[
              {
                text: 'Open Contact',
                onPress: () => handleOpenContact(contact),
              },
              {
                text: 'Send Message',
                icon: ICONS.PaperPlaneLight,
                onPress: () =>
                  dispatch(
                    NAVIGATE_MODAL({
                      screen: ModalScreens.EngageSendCommunication,
                      params: {
                        key,
                        flatIds: [flat.id],
                        propertyId: property.id,
                        contactId: contact.id,
                        mode: 'singleFlat',
                      },
                    }),
                  ),
              },
              '-',
              {
                text: 'Archive Contact',
                onPress: () =>
                  dispatch(
                    doEngageArchiveContact({
                      key,
                      propertyId: property.id,
                      flatId: flat.id,
                      contactId: contact.id,
                    }),
                  ),
                icon: ICONS.TrashCanLight,
                destructive: true,
              },
            ]}
          />
        ))}
      </Card>

      {archivedContacts === null && (
        <Card>
          <CardRow
            label={'See Archived Contacts'}
            labelColor={theme.colors.blue}
            onPress={() =>
              dispatch(
                doEngageFlatArchivedContactsLoad({
                  key,
                  propertyId: property.id,
                  flatId: flat.id,
                }),
              )
            }
          />
        </Card>
      )}

      {archivedContacts !== null && (
        <Card
          label={pluralise(
            archivedContacts.length,
            '1 Archived Contact',
            '{x} Archived Contacts',
            'No Archived Contacts',
          )}
        >
          {!archivedContacts.length && <CardRow value="No Archived Contacts" />}
          {archivedContacts.map((contact) => (
            <EngageContactCard
              key={contact.id}
              contact={contact}
              flatListContact={false}
              showAddedInfo
              showArchivedInfo
              menuOptions={[
                {
                  text: 'Unarchive Contact',
                  onPress: () => handleUnarchiveContact(contact.id),
                },
              ]}
            />
          ))}
        </Card>
      )}
    </PageScrollView>
  );
};
